import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

interface Language {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
}

// Exportiere die Sprachenliste für andere Komponenten
export const languages: Language[] = [
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski', flag: '🇵🇱' },
  { code: 'ro', name: 'Romanian', nativeName: 'Română', flag: '🇷🇴' },
];

interface LanguageSelectorProps {
  onLanguageChange?: (langCode: string) => void;
  className?: string;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({ onLanguageChange, className }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLanguageChange = async (langCode: string) => {
    try {
      console.log('Changing language from', i18n.language, 'to', langCode);
      
      // Force language change and wait for it to complete
      await i18n.changeLanguage(langCode);
      
      // Explicitly save to localStorage to prevent fallback
      localStorage.setItem('i18nextLng', langCode);
      
      // Update document language attribute for accessibility
      document.documentElement.lang = langCode;
      
      // Force a re-render by triggering a storage event
      window.dispatchEvent(new Event('storage'));
      
      setIsOpen(false);
      
      // Call the optional callback to persist the language preference
      if (onLanguageChange) {
        onLanguageChange(langCode);
      }
      
      console.log('Language successfully changed to:', langCode, 'Current language:', i18n.language);
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };
  
  return (
    <div className={`relative ${className || ''}`} ref={dropdownRef}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-secondary hover:text-primary hover:bg-surface-raised transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
        aria-label={`Current language: ${currentLanguage.nativeName}. Click to change language.`}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <span className="text-lg" role="img" aria-hidden="true">{currentLanguage.flag}</span>
        <span className="hidden sm:block">{currentLanguage.nativeName}</span>
        <ChevronDownIcon 
          className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          aria-hidden="true"
        />
      </button>
      
      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-surface-raised ring-1 ring-black ring-opacity-5 z-[100]">
          <div className="py-1" role="menu" aria-orientation="vertical">
            {languages.map(lang => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`flex items-center w-full px-4 py-2 text-sm text-left hover:bg-surface-inset transition-colors ${
                  lang.code === currentLanguage.code
                    ? 'bg-[var(--tone-accent-bg)] text-accent-ink'
                    : 'text-secondary'
                }`}
                role="menuitem"
                aria-current={lang.code === currentLanguage.code ? 'true' : 'false'}
              >
                <span className="mr-3 text-lg" role="img" aria-hidden="true">{lang.flag}</span>
                <div className="flex flex-col">
                  <span className="font-medium">{lang.nativeName}</span>
                  <span className="text-xs text-muted">{lang.name}</span>
                </div>
                {lang.code === currentLanguage.code && (
                  <span className="ml-auto text-accent-ink" aria-hidden="true">✓</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;